import Logger from '../lib/logger';
import PluginBase from './base';
import ScriptLoader from '../lib/script_loader';
import { getCanonicalUrl } from '../lib/utils';
import Events, { EventTypes } from '../lib/events';

export default class AmazonBidding extends PluginBase {
  onBidManagerCreated() {
    if (this.biddingWithAmazon()) {
      Logger.log('Installing plugin: Amazon Bidding');

      if (!this.app.hasReinstalled) {
        this.loadAmazonScript();
        Events.emit(EventTypes.amazonBiddingSetUp);
      }

      this.app.bidManager.addBiddingPartner(this);
    }
  }

  /**
   * Determine whether we are bidding with Amazon
   * @return {boolean}
   */
  biddingWithAmazon() {
    return (
      this.app.settings.prebid.biddingEnabled &&
      this.app.settings.prebid.amazonPubId &&
      (this.app.settings.prebid.amazonUrl || this.app.settings.prebid.loadAmazonScriptInConcertAds === false)
    );
  }

  /**
   * Loading the Amazon script
   * We are using the Bid Manager beforeFirstBid method to make sure the script loads before we start bidding
   * @return {undefined}
   */
  loadAmazonScript() {
    const dependency = {
      url: this.app.settings.prebid.amazonUrl,
      timeout: this.app.settings.prebid.amazonScriptTimeout || 3000,
    };

    this.app.bidManager.beforeFirstBid(() => {
      return new Promise((resolve, reject) => {
        if (
          this.app.settings.prebid.loadAmazonScriptInConcertAds === false ||
          document.querySelector(`script[src="${dependency.url}"]`)
        ) {
          Logger.log(`Amazon script already exists in the DOM.`);
          setUpAmazon(this.app);
          resolve();
        } else {
          new ScriptLoader()
            .load(dependency)
            .then(url => {
              Logger.log(`Amazon (TAM) script ${url} has loaded successfully.`);
              setUpAmazon(this.app);
              resolve();
            })
            .catch(error => {
              Logger.error(`Error loading Amazon script: ${error}`);
              // We still want to set up Amazon even if the script takes a long time to load
              setUpAmazon(this.app);
              reject(error);
            });
        }
      });
    });
  }

  /**
   * Build up the Amazon bid queues
   * @param  {Array} array of bids
   * @return {undefined}
   */
  buildBiddingQueues(queue) {
    const biddingQueue = [];

    queue.forEach(bid => {
      biddingQueue.push({
        slotID: bid.slot.id,
        slotName: bid.app.settings.slug,
        sizes: bid.slot.sizes,
      });
    });

    return biddingQueue;
  }

  /**
   * Fetch bids from Amazon.
   * @type {Promise}
   */
  fetchBidsFor({ queueOfBids, timeout }) {
    let bidsFetched = false;
    if (typeof apstag === 'undefined' || typeof apstag.setDisplayBids !== 'function') return;

    return new Promise(resolve => {
      const slots = this.buildBiddingQueues(queueOfBids);
      const slotIds = slots.map(slot => slot?.slotID);

      // Re-init per auction so Amazon gets the Optimera segments current at bid time
      // (they arrive after the first init). Mirrors Atlas initApstag() in requestAmazonBids().
      if (sendOptimeraToAmazon(this.app)) {
        Logger.log('Re-initializing Amazon with current Optimera segments.');
        initializeAmazon(this.app);
      }

      Logger.log(`Fetching bids for the following slots with Amazon: [${slotIds}]`);

      apstag.fetchBids(
        {
          slots,
        },
        function() {
          Logger.log(`Amazon bids have been fetched`);
          bidsFetched = true;
          resolve();
        }
      );

      // Set failsafe timeout in case things go awry
      setTimeout(() => {
        if (!bidsFetched) {
          Logger.log(`Amazon took longer than ${timeout} ms to fetch bids so we are calling the failsafe timeout`);
          resolve();
        }
      }, timeout);
    }).catch(e => {
      Logger.log(`There was an error fetching bids with Amazon: ${e}`);
    });
  }

  /**
   * Add Amazon targeting to bids
   */
  addTargeting() {
    if (typeof apstag === 'undefined' || typeof apstag.setDisplayBids !== 'function') return;
    Logger.log('Adding Amazon targeting to bids.');
    apstag.setDisplayBids();
  }
}

/**
 * Initialize apstag library and set config to handle setting bid targeting.
 */
function setUpAmazon(app) {
  const {
    amazonPubId,
    amazonNCAEnabled,
    setAmazonNCAAttributes,
    amazonNCASiteTag,
    waitForHyphenSocialSetupBeforeNCASetup,
  } = app.settings.prebid;

  Logger.log(`Setting up Amazon with pubId: ${amazonPubId}.`);
  try {
    if (!amazonPubId) return;
    if (amazonNCAEnabled) {
      setupNCABootstrapandNamespace(amazonPubId);
    } else {
      setUpLegacyNamespace();
    }
    initializeAmazon(app);

    if (!amazonNCAEnabled) return;

    // On Hyphen Social sites we have to wait for their setup before finishing NCA Setup
    if (!waitForHyphenSocialSetupBeforeNCASetup || window.hyphenSocialComplete) {
      setNCAAttributesAndEnableNativeContent(setAmazonNCAAttributes, amazonNCASiteTag);
    } else {
      window.addEventListener('NCAReady', e => {
        Logger.log(`Received the NCAReady event: ${e}`);
        setNCAAttributesAndEnableNativeContent(setAmazonNCAAttributes, amazonNCASiteTag);
      });
    }
  } catch (e) {
    Logger.error(`There was an error setting up Amazon Bidding: ${e}`);
  }
}

/**
 * Setting up Amazon Namespace and Methods for sites using legacy implementation
 * (i.e., those not using Amazon NCA)
 */
function setUpLegacyNamespace() {
  window.apstag = window.apstag || {
    _Q: [],
    init: function() {
      apstag._Q.push(['i', arguments, new Date().getTime()]);
    },
    fetchBids: function() {
      apstag._Q.push(['f', arguments, new Date().getTime()]);
    },
    setDisplayBids: function() {},
    targetingKeys: function() {
      return [];
    },
  };
}

/**
 * Setting up Amazon Bootstrap and Namespace for sites using Amazon NCA
 * @param {Number} amazonPubId
 */
function setupNCABootstrapandNamespace(amazonPubId) {
  const amazonPubIdString = typeof amazonPubId === 'number' ? amazonPubId.toString() : amazonPubId;

  const aps = bootstrapNCA(amazonPubIdString);
  setupNCANamespace(aps);
}

/**
 * Initializing Amazon. Safe to call again to refresh the signals.
 * @param {Object} app
 */
export function initializeAmazon(app) {
  const { amazonPubId, amazonNCAEnabled, amazonNCAPubUUID } = app.settings.prebid;
  const apstagInitObj = {
    pubID: amazonPubId,
    adServer: 'googletag',
    videoAdServer: 'connatix',
    deals: true,
  };

  const site = {};

  if (amazonNCAEnabled) {
    Logger.log('Amazon NCA signals are being added to the init object.');
    site.page = getCanonicalUrl();
    site.publisher = {
      id: amazonNCAPubUUID,
    };
  }

  if (sendOptimeraToAmazon(app)) {
    Object.assign(site, getOptimeraSiteSignals(app));
  }

  if (Object.keys(site).length) {
    apstagInitObj.signals = {
      ortb2: {
        site,
      },
    };
  }

  apstag.init(apstagInitObj);
}

/**
 * Whether Optimera segments should be sent to Amazon (settings.optimera.amazon.enabled).
 * Mirrors Atlas `amazon_send_optimera_ortb2`.
 * @param {Object} app
 * @return {Boolean}
 */
export function sendOptimeraToAmazon(app) {
  return app.settings.optimera?.amazon?.enabled === true;
}

/**
 * Build the Optimera part of ortb2.site from the page-level Optimera key (default `opti_page`),
 * filtered by settings.optimera.amazon.segments when that list is not empty.
 * Mirrors Atlas getAmazonOrtb2().
 * @param {Object} app
 * @return {Object}
 */
export function getOptimeraSiteSignals(app) {
  const { optimera } = app.settings;
  const key = optimera.hardcodedTargeting?.pageTargetingKey ?? 'opti_page';
  const value = app.getVariable(key);
  const segments = value === undefined || value === null ? [] : [].concat(value).map(String);
  const allowedSegments = optimera.amazon.segments || [];

  return {
    domain: window.location.hostname,
    ext: {
      data: {
        id: allowedSegments.length ? segments.filter(segment => allowedSegments.includes(segment)) : segments,
      },
    },
  };
}

/**
 * If Amazon NCA is enabled,
 * 1) Set up Amazon NCA attributes (only if requested - this is usually done in the CMS)
 * 2) Enable Native Content
 * @param {Boolean} setAmazonNCAAttributes
 * @param {String} amazonNCASiteTag
 */
function setNCAAttributesAndEnableNativeContent(setAmazonNCAAttributes, amazonNCASiteTag) {
  setNCAAttributes(setAmazonNCAAttributes, amazonNCASiteTag);
  enableNativeContent();
}

/**
 * Finds all featured Amazon links in the page and appends the Amazon NCA data attributes
 * @param {Boolean} setAmazonNCAAttributes
 * @param {String} amazonNCASiteTag
 */
function setNCAAttributes(setAmazonNCAAttributes, amazonNCASiteTag) {
  if (!setAmazonNCAAttributes || !amazonNCASiteTag) {
    return;
  }

  Logger.log(`Amazon NCA data attributes are being appended with the site tag ${amazonNCASiteTag}.`);

  const amazonLinks = document.querySelectorAll("[href*='amazon']");

  amazonLinks.forEach(link => {
    const { asin, tag, subtag } = extractAmazonDetails(link.href, amazonNCASiteTag);

    if (!asin) return;

    link.setAttribute('data-aps-asin', asin);
    link.setAttribute('data-aps-asc-tag', tag);
    link.setAttribute('data-aps-asc-subtag', subtag);
  });
}

/**
 * Enabling Native Content for APS NCA Integration
 */
function enableNativeContent() {
  apstag.queue.push(function() {
    window.apstag.nativeContent().enable();
  });
}

/**
 * Extracts the Amazon Details from a product URL
 * @param {String} url
 * @param {String} amazonNCASiteTag
 * @returnas {Object}
 */
function extractAmazonDetails(url, amazonNCASiteTag) {
  try {
    const AMAZON_DETAILS_PATTERN = /(?:dp|gp\/product|gp\/offer-listing|gp\/aw|gp\/video)\/([A-Z0-9]{10})(?:.*?[?&]tag=([^&]+))?(?:.*?[?&]subtag=([^&]+))?/;
    const match = url.match(AMAZON_DETAILS_PATTERN);
    const defaultSubTag = 'nca';

    if (!match) {
      return { asin: null, tag: null, subtag: null };
    }

    const asin = match[1];
    const tag = match[2] || amazonNCASiteTag;
    const subtag = match[3] || defaultSubTag;

    return { asin, tag, subtag };
  } catch (error) {
    Logger.error(`Error extracting Amazon details for data attributes`, error);
    return { asin: null, tag: null, subtag: null };
  }
}

/**
 * Amazon Bootstrap NCA Implementation
 * @param {String} amazonPubIdString
 * @returns Object
 */
function bootstrapNCA(amazonPubIdString) {
  const aps = ((e = amazonPubIdString) => {
    (window._aps = window._aps || new Map()),
      window._aps.has(e) || window._aps.set(e, { queue: [], store: new Map([['listeners', new Map()]]) });
    let t = {
      accountID: e,
      record: function(e, t) {
        return new Promise((r, n) => {
          window._aps.get(this.accountID).queue.push(new CustomEvent(e, { detail: { ...t, resolve: r, reject: n } }));
        });
      },
      read: function(e) {
        return this.record('bootstrap/store/willRead', { name: e }), window._aps.get(this.accountID).store.get(e);
      },
      run: function(e, t) {
        window._aps
          .get('_system')
          ?.store.get('listeners')
          .has(e)
          ? this.record(e, t)
          : this.record('bootstrap/run/willDrop', { name: e, detail: t });
      },
      react: function(e, t) {
        window._aps
          .get(this.accountID)
          .store.get('listeners')
          .set(e, t);
        this.record('bootstrap/react/didExecute', { name: e });
      },
    };
    return t.record('bootstrap/version/declare', { version: 5 }), t;
  })();

  return aps;
}

/**
 * Set up Amazon NCA Namespace
 * @param {Object} aps
 */
function setupNCANamespace(aps) {
  ((t = aps) => {
    window.apstag = window.apstag || {
      init: function(e, r) {
        t.record('legacy/init/enqueue', { config: e, callback: r });
      },
      fetchBids: function(e, r) {
        t.record('legacy/fetchBids/enqueue', { bidConfig: e, callback: r });
      },
      setDisplayBids: function() {
        t.run('legacy/setDisplayBids/enqueue', { arguments });
      },
      targetingKeys: function() {
        return t.read('legacy/targetingKeys') ?? [];
      },
      rpa: function(e, r, n) {
        t.record('legacy/rpa/enqueue', {
          tokenConfig: e,
          callback: r,
          setCookie: n,
        });
      },
      upa: function(e, r, n) {
        t.record('legacy/upa/enqueue', {
          tokenConfig: e,
          callback: r,
          setCookie: n,
        });
      },
      dpa: function(e) {
        t.record('legacy/dpa/enqueue', { callback: e });
      },
    };
    t.record('bootstrapExtAPStag/version/declare', { version: 1 });
  })();

  ((t = aps) => {
    window.apstag = {
      ...window.apstag,
      queue: {
        push: function(e) {
          t.record('legacy/queue/push', { callback: e });
        },
      },
    };
    t.record('bootstrapExtQueue/version/declare', { version: 1 });
  })();

  ((t = aps) => {
    window.apstag = {
      ...window.apstag,
      nativeContent: function() {
        return {
          enable: function() {
            t.record('nativeCommerceAds/service/enable');
          },
          registerHook: function(e, r) {
            if (e === 'nativeContent.updateClickUrl') {
              t.react('nativeCommerceAds/clickUrl/update', k => {
                return {
                  status: 'completed',
                  value: { url: r(k?.detail?.url, k?.detail?.element) },
                };
              });
            } else {
              throw new Error('Unsupported event type');
            }
          },
        };
      },
    };
    t.record('bootstrapExtNCA/version/declare', { version: 1 });
  })();
}
